import Image from "next/image";

import cn from "classnames";

import { Link } from "@/components/link/link.component";
import { Text } from "@/components/text/text.component";

import { appendQueryString } from "@/utils/url.util";

import type { GetAdtPlusAppPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

export const AppDownloadCallout = ({ block }: AppDownloadCalloutProps) => {
  if (!block) return null;

  const qrCodeUrl = block.qrCode?.asset
    ? urlFor(block.qrCode).width(360).height(360).url()
    : null;

  const featuredImageUrl = block.featuredImage?.asset
    ? urlFor(block.featuredImage).width(2400).fit("max").url()
    : null;

  const featuredImageDimensions = block.featuredImage?.asset?.metadata
    ?.dimensions ?? { width: 2400, height: 1800 };

  const storeButtons = (block.storeButtons ?? []).filter((item) =>
    Boolean(item?.asset),
  );

  const renderStoreButtons = () => {
    if (!storeButtons.length) return null;

    return (
      <div className="flex flex-col gap-3">
        {storeButtons.map((item, index) => {
          const href =
            appendQueryString(item.link?.url, item.link?.queryString) ?? "#";
          const openInNewTab = item.link?.openInNewTab ?? false;
          const target = openInNewTab ? "_blank" : undefined;

          return (
            <Link
              key={item._key ?? `${href}-${index}`}
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              aria-label={item.alt ?? "App download badge"}
              className="focus-visible:ring-secondary inline-flex rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              data-testid={`app-download-callout-store-button-${index}`}>
              <Image
                src={urlFor(item).height(120).url()}
                alt={item.alt ?? "App download badge"}
                width={160}
                height={48}
                className="h-12 w-auto"
              />
            </Link>
          );
        })}
      </div>
    );
  };

  const imageOnLeft = block.imagePosition === "left";

  return (
    <section
      data-testid="app-download-callout"
      className="w-full bg-white px-6 py-12 lg:px-8 lg:py-20">
      <div className="container mx-auto">
        <div
          className={cn(
            "flex w-full flex-col items-center gap-[32px] lg:grid lg:grid-cols-2 lg:items-center lg:gap-[32px]",
          )}>
          {featuredImageUrl ? (
            <div
              className={cn(
                "flex w-full justify-center lg:justify-start",
                imageOnLeft ? "lg:order-1 lg:justify-end" : "lg:order-2",
              )}>
              <Image
                src={featuredImageUrl}
                alt={block.featuredImage?.alt ?? "ADT+ app and cameras"}
                width={featuredImageDimensions.width}
                height={featuredImageDimensions.height}
                sizes="(max-width: 1023px) 100vw, 50vw"
                className="h-auto max-h-[500px] w-full max-w-[280px] object-contain lg:max-w-full"
                data-testid="app-download-callout-image"
              />
            </div>
          ) : null}

          <div
            className={cn(
              "flex w-full flex-col items-stretch",
              imageOnLeft
                ? "lg:order-2 lg:items-start"
                : "lg:order-1 lg:items-end",
            )}>
            <div className="flex w-full flex-col gap-6 text-left lg:max-w-lg lg:gap-8">
              {block.title ? (
                <Text
                  variant="h2"
                  component="h2"
                  className="text-greyscale-headline max-w-176"
                  data-testid="app-download-callout-title">
                  {block.title}
                </Text>
              ) : null}

              {block.description ? (
                <Text
                  variant="body1"
                  className="text-greyscale-body max-w-md"
                  data-testid="app-download-callout-description">
                  {block.description}
                </Text>
              ) : null}

              {(qrCodeUrl || storeButtons.length > 0) && (
                <div
                  className="flex flex-wrap items-center gap-6"
                  data-testid="app-download-callout-actions">
                  {qrCodeUrl ? (
                    <Image
                      src={qrCodeUrl}
                      alt={
                        block.qrCode?.alt ?? "QR code to download the ADT+ app"
                      }
                      width={120}
                      height={120}
                      className="h-30 w-30 shrink-0"
                      data-testid="app-download-callout-qr"
                    />
                  ) : null}

                  {renderStoreButtons()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

type PageBuilderBlocks = NonNullable<
  NonNullable<GetAdtPlusAppPageQueryResult>["pageBuilder"]
>;

type AppDownloadCalloutBlock = Extract<
  PageBuilderBlocks[number],
  { _type: "appDownloadCallout" }
>;

type AppDownloadCalloutProps = {
  block: AppDownloadCalloutBlock | null | undefined;
};
